import React, { useEffect, useState } from 'react';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ExecutiveAgentPage() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [examples, setExamples] = useState([]);

  useEffect(() => {
    const loadExamples = async () => {
      try {
        const res = await api.get('/executive-agent/examples');
        setExamples(res.data?.examples || []);
      } catch {
        setExamples([]);
      }
    };
    loadExamples();
  }, []);

  const submit = async (queryText) => {
    const q = (queryText ?? text).trim();
    if (!q) return;
    setLoading(true);
    setConversation((prev) => [...prev, { role: 'user', text: q }]);
    setText('');
    try {
      const res = await api.post('/executive-agent/test', { text: q });
      const answer = res.data?.answer || 'Sin respuesta.';
      setConversation((prev) => [...prev, { role: 'agent', text: answer }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error consultando el agente';
      toast.error(msg);
      setConversation((prev) => [...prev, { role: 'agent', text: `❌ ${msg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="space-y-6" data-testid="executive-agent-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Agente Ejecutivo</h1>
          <p className="text-sm text-slate-500">
            Pregunta en lenguaje natural · Datos en tiempo real desde MongoDB
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversación */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Conversación</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="space-y-3 mb-4 min-h-[280px] max-h-[440px] overflow-y-auto pr-2"
              data-testid="conversation-container"
            >
              {conversation.length === 0 && (
                <div className="text-sm text-slate-400 text-center py-10">
                  Escribe una pregunta o usa un ejemplo de la derecha →
                </div>
              )}
              {conversation.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${m.role}-${i}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2 text-sm whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-800 border border-slate-200'
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Consultando datos…
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ej: ¿Cómo está la operación hoy?"
                rows={2}
                className="flex-1 resize-none"
                data-testid="agent-query-input"
                disabled={loading}
              />
              <Button
                onClick={() => submit()}
                disabled={loading || !text.trim()}
                className="self-end"
                data-testid="agent-send-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ejemplos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              Ejemplos sugeridos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="examples-list">
              {examples.map((q, i) => (
                <button
                  key={i}
                  onClick={() => submit(q)}
                  disabled={loading}
                  className="w-full text-left px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition text-sm disabled:opacity-50"
                  data-testid={`example-query-${i}`}
                >
                  {q}
                </button>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-slate-200">
              <Badge variant="outline" className="text-xs">
                Tip
              </Badge>
              <p className="text-xs text-slate-500 mt-2">
                Este agente también responde por WhatsApp si tu número está
                registrado como admin o supervisor.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
